import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { apiUtils } from '../utils';

const ScanProgressContext = createContext();

/**
 * Transforms raw scan data (from API or WebSocket) into a consistent format for the UI
 */
const transformApiScanData = (payload) => {
	if (!payload) return null;
	// This handles both the direct payload and the { data: payload } structure
	const scanData = payload.data ? payload.data : payload;

	return {
		scanId: scanData._id || scanData.scanId,
		status: scanData.status,
		platform: scanData.platform,
		totalWindows: scanData.totalWindows || 0,
		jobsCreated: scanData.jobsCreated || 0,
		progressPercent: scanData.progress?.overallProgress || 0,
		postsScanned: scanData.progress?.postsAnalyzed || 0,
		postsTotal: scanData.progress?.postsFound || 0,
		flaggedCount: scanData.progress?.flaggedPosts || 0,
		clientName: scanData.client?.name || "",
		accountHandle: scanData.account?.handle || scanData.account?.username || "",
		progress: scanData.progress,
		windows: scanData.windows,
		results: scanData.results,
		startedAt: scanData.startedAt,
		completedAt: scanData.completedAt,
		dateRange: scanData.dateRange,
		statusMessage:
			scanData.status === 'analyzing'
				? `Analyzing posts... (${scanData.progress?.postsAnalyzed || 0}/${scanData.progress?.postsFound || 0})`
				: scanData.status === 'processing'
					? `Scanning window ${scanData.progress?.currentWindow || 0} of ${scanData.progress?.totalWindows || 0}`
					: scanData.status === 'completed'
						? 'Scan completed'
						: 'Initializing scan...',
		isLoading: false, // Ensure loading is reset on update
	};
};


/**
 * ScanProgressProvider - Manages scan progress data and WebSocket subscriptions
 * 
 * This provider handles all scan progress updates through WebSocket subscriptions
 * and provides fallback polling when WebSocket is not available.
 */
export function ScanProgressProvider({ children }) {
	const [scanProgressData, setScanProgressData] = useState(new Map()); // scanId -> progressData
	const [activeScanIds, setActiveScanIds] = useState(new Set());

	/**
	 * Fetch scan progress via HTTP (fallback or initial load)
	 */
	const fetchScanProgress = useCallback(async (scanId) => {
		if (!scanId) return;

		try {
			const response = await apiUtils.get(`/api/scans/${scanId}/progress`);

			if (!response.ok) {
				// If scan not found (404), stop polling by marking as complete with error
				if (response.status === 404) {
					setScanProgressData(prev => new Map(prev).set(scanId, {
						...(prev.get(scanId) || {}),
						status: 'failed',
						isLoading: false,
						error: 'Scan not found',
					}));
					return null;
				}
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();
			const transformedData = transformApiScanData(data);

			if (transformedData) {
				setScanProgressData(prev => new Map(prev).set(scanId, transformedData));
			}

			return transformedData;
		} catch (error) {
			console.error(`Error fetching scan progress for ${scanId}:`, error);

			setScanProgressData(prev => new Map(prev).set(scanId, {
				...(prev.get(scanId) || {}),
				isLoading: false,
				error: error.message,
			}));

			throw error;
		}
	}, []);

	/**
	 * Subscribe to a specific scan's progress updates
	 */
	const subscribeScan = useCallback((scanId) => {
		if (!scanId) return () => { };

		setActiveScanIds(prev => {
			if (prev.has(scanId)) return prev; // keep identity stable if already tracked
			const next = new Set(prev);
			next.add(scanId);
			return next;
		});

		// Initialize progress data if not present. Use a FUNCTIONAL update so this
		// callback does NOT depend on `scanProgressData`. Depending on it made
		// subscribeScan change identity on every data update, which re-ran the
		// `useEffect(() => subscribeScan(scanId), [scanId, subscribeScan])` consumer
		// effect - fetching + re-subscribing in an infinite loop (≈4 /progress
		// requests per second, constant re-renders, UI churn/flicker).
		setScanProgressData(prev => {
			if (prev.has(scanId)) return prev;
			const next = new Map(prev);
			next.set(scanId, {
				clientName: "",
				progressPercent: 0,
				postsScanned: 0,
				postsTotal: 0,
				platform: "",
				estimatedTime: null,
				riskFilter: "Standard",
				flaggedCount: 0,
				statusMessage: "Initializing scan...",
				actionLinks: [],
				status: "pending",
				scanId: null,
				startedAt: null,
				completedAt: null,
				isLoading: true,
				error: null,
			});
			return next;
		});

		// Fetch initial data
		fetchScanProgress(scanId);

		return () => {
			setActiveScanIds(prev => {
				if (!prev.has(scanId)) return prev;
				const next = new Set(prev);
				next.delete(scanId);
				return next;
			});
		};
	}, [fetchScanProgress]);

	/**
	 * Retry a failed scan using smart retry logic
	 */
	const retryScan = useCallback(async (scanId, options = {}) => {
		if (!scanId) return;

		try {
			const response = await apiUtils.post(`/api/scans/${scanId}/retry`, options);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			// Reset progress data for retry
			setScanProgressData(prev => new Map(prev).set(scanId, {
				...(prev.get(scanId) || {}),
				status: "pending",
				progressPercent: 0,
				postsScanned: 0,
				flaggedCount: 0,
				statusMessage: "Retrying scan...",
				completedAt: null,
				error: null,
			}));

			return await response.json();
		} catch (error) {
			console.error(`Error retrying scan ${scanId}:`, error);

			setScanProgressData(prev => new Map(prev).set(scanId, {
				...(prev.get(scanId) || {}),
				error: error.message,
			}));

			throw error;
		}
	}, []);

	/**
	 * Get scan progress data
	 */
	const getScanProgress = useCallback((scanId) => {
		const data = scanProgressData.get(scanId);
		return data || null;
	}, [scanProgressData]);

	/**
	 * Update scan progress data from WebSocket message
	 */
	const updateScanProgress = useCallback((scanId, updates) => {
		setScanProgressData(prev => {
			const current = prev.get(scanId) || {};
			const newData = {
				...current,
				...updates,
				isLoading: false,
				error: null,
			};
			const newMap = new Map(prev);
			newMap.set(scanId, newData);
			return newMap;
		});
	}, []);

	// Memoize so consumers don't re-render on every provider render (the value was
	// a fresh object each render, churning every useScanProgress consumer and
	// tearing down their polling intervals before they could fire).
	const contextValue = useMemo(() => ({
		subscribeScan,
		getScanProgress,
		updateScanProgress,
		fetchScanProgress,
		retryScan,
		activeScanIds: Array.from(activeScanIds),
	}), [subscribeScan, getScanProgress, updateScanProgress, fetchScanProgress, retryScan, activeScanIds]);

	return (
		<ScanProgressContext.Provider value={contextValue}>
			{children}
		</ScanProgressContext.Provider>
	);
}

/**
 * Hook to access scan progress context
 */
export function useScanProgressContext() {
	const context = useContext(ScanProgressContext);
	if (!context) {
		throw new Error('useScanProgressContext must be used within a ScanProgressProvider');
	}
	return context;
}

/**
 * Hook for individual scan progress management
 * 
 * This is the new replacement for useScanProgressWebSocket
 */
export function useScanProgress(scanId, options = {}) {
	const { autoRefresh = true } = options;
	const { subscribeScan, getScanProgress, updateScanProgress, fetchScanProgress, retryScan } = useScanProgressContext();
	const [localError, setLocalError] = useState(null);

	// Handle WebSocket messages for this scan
	const handleScanMessage = useCallback((message) => {
		const { type, payload } = message;

		// Handle new state-based updates
		if (type === 'scan.state.update') {
			// Transform the full state into the format expected by the frontend
			const transformedData = transformApiScanData(payload);

			if (transformedData) {
				updateScanProgress(payload.scanId, transformedData);
			}
		}
		// Handle async job creation progress events
		else if (type === 'job_creation_progress') {
			updateScanProgress(payload.scanId, {
				...payload,
				status: 'queuing_jobs', // Ensure status is set correctly
				totalWindows: payload.total || 0,
				jobsCreated: payload.created || 0,
				progress: {
					...(getScanProgress(payload.scanId)?.progress || {}),
					jobCreation: payload.percentage || 0
				}
			});
		}
		// Keep legacy event handling for backward compatibility
		else if (type === 'scan.progress' || type === 'scan.status' ||
			type === 'analysis.progress' || type === 'window.analysis.progress') {
			updateScanProgress(payload.scanId, payload);
		}
	}, [updateScanProgress, getScanProgress]);

	// Subscribe to WebSocket updates
	useSubscription(
		scanId ? `scan:${scanId}` : null,
		handleScanMessage,
		{ enabled: !!scanId }
	);

	// Subscribe to this scan in the context
	useEffect(() => {
		if (!scanId) return;

		const unsubscribe = subscribeScan(scanId);
		return unsubscribe;
	}, [scanId, subscribeScan]);

	// Get current progress data
	const rawData = getScanProgress(scanId);

	const progressData = rawData || {
		clientName: "",
		progressPercent: 0,
		postsScanned: 0,
		postsTotal: 0,
		platform: "",
		estimatedTime: null,
		riskFilter: "Standard",
		flaggedCount: 0,
		statusMessage: "Initializing scan...",
		actionLinks: [],
		status: "pending",
		scanId: scanId,
		startedAt: null,
		completedAt: null,
		isLoading: true,
		error: null,
	};

	// Auto-refresh via polling as fallback
	useEffect(() => {
		if (!scanId || !autoRefresh) return;

		const shouldPoll = progressData.status === "initializing" ||
			progressData.status === "queuing_jobs" ||
			progressData.status === "processing" ||
			progressData.status === "analyzing" ||
			progressData.status === "pending";

		if (shouldPoll) {
			const interval = setInterval(() => {
				if (document.hidden) return; // no polling from background tabs
				fetchScanProgress(scanId).catch(err => {
					setLocalError(err.message);
				});
			}, 5000); // Poll every 5 seconds as fallback

			return () => clearInterval(interval);
		}
	}, [scanId, autoRefresh, progressData.status, fetchScanProgress]);

	// Wrapper for retryScan with error handling
	const handleRetryScan = useCallback(async () => {
		setLocalError(null);
		try {
			return await retryScan(scanId);
		} catch (error) {
			setLocalError(error.message);
			throw error;
		}
	}, [scanId, retryScan]);

	// Derived state
	const isScanning = progressData.status === "initializing" ||
		progressData.status === "queuing_jobs" ||
		progressData.status === "processing" ||
		progressData.status === "analyzing" ||
		progressData.status === "pending";
	const isCompleted = progressData.status === "completed";
	const isFailed = progressData.status === "failed";
	const error = progressData.error || localError;

	return {
		progressData,
		isLoading: progressData.isLoading,
		error,
		fetchProgress: () => fetchScanProgress(scanId),
		retryScan: handleRetryScan,
		isScanning,
		isCompleted,
		isFailed,
	};
}

export default ScanProgressContext;
